import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { getApiBaseUrl } from './utils/api';
import './Utilization.css';

const Utilization = () => {
  const { selectedTenant, authenticatedFetch } = useAuth();
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tooltip, setTooltip] = useState({ show: false, x: 0, y: 0, text: '' });
  const fetchingRef = useRef(false);

  // PCAP-related state
  const [pcapDocuments, setPcapDocuments] = useState([]);
  const [pcapLoading, setPcapLoading] = useState(false);
  const [pcapError, setPcapError] = useState(null);
  const [selectedPcapId, setSelectedPcapId] = useState('');
  const [pcapConnections, setPcapConnections] = useState([]);
  const [pcapZoom, setPcapZoom] = useState(1);
  const [pcapNodes, setPcapNodes] = useState([]);
  const [dragIp, setDragIp] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [filterBroadcastMulticast, setFilterBroadcastMulticast] = useState(true);
  const [filterRfc1918, setFilterRfc1918] = useState(false);
  const [selectedIpFilter, setSelectedIpFilter] = useState('');
  const [selectedPortFilter, setSelectedPortFilter] = useState('');
  const [viewAsFirewallRules, setViewAsFirewallRules] = useState(false);
  const [hoveredNodeIp, setHoveredNodeIp] = useState(null);
  const [hoveredNodePosition, setHoveredNodePosition] = useState({ x: 0, y: 0 });
  const [pcapGraphWidth, setPcapGraphWidth] = useState(800);
  const [pcapGraphHeight, setPcapGraphHeight] = useState(300);
  const pcapSvgRef = useRef(null);
  const [hasGraphConfig, setHasGraphConfig] = useState(false);

  // Check if UBNT or FortiEndpoint is configured (graph requires this)
  useEffect(() => {
    if (!selectedTenant) {
      setHasGraphConfig(false);
      return;
    }
    const check = async () => {
      try {
        const res = await authenticatedFetch(
          `${getApiBaseUrl()}/api/tenant-settings?tenant=${encodeURIComponent(selectedTenant.name)}`
        );
        if (res.ok) {
          const data = await res.json();
          const hasUBNT = Boolean(data.UBNTAPIKey && data.UBNTAPIKey.trim() &&
            data.UBNTAPIEndPoint && data.UBNTAPIEndPoint.trim());
          const hasFortiEndpoint = Boolean(data.FortiEndpoint && data.FortiEndpoint.trim());
          setHasGraphConfig(hasUBNT || hasFortiEndpoint);
        } else {
          setHasGraphConfig(false);
        }
      } catch {
        setHasGraphConfig(false);
      }
    };
    check();
  }, [selectedTenant?.name, authenticatedFetch]);

  const fetchNetworkClients = useCallback(async () => {
    if (!selectedTenant) {
      setError('No tenant selected');
      setLoading(false);
      return;
    }

    // Prevent concurrent fetches
    if (fetchingRef.current) {
      return;
    }

    try {
      fetchingRef.current = true;
      setLoading(true);
      setError(null);

      const response = await authenticatedFetch(
        `${getApiBaseUrl()}/api/networkclients?tenant=${encodeURIComponent(selectedTenant.name)}`
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.error || 'Failed to fetch network clients');
        setTimeline([]);
      } else {
        setTimeline(Array.isArray(data.timeline) ? data.timeline : []);
      }
    } catch (e) {
      console.error('Error fetching network clients:', e);
      setError('Network error. Please try again.');
      setTimeline([]);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [selectedTenant?.name, authenticatedFetch]);

  useEffect(() => {
    if (selectedTenant?.name && hasGraphConfig) {
      fetchNetworkClients();
    } else {
      setTimeline([]);
      setLoading(false);
    }
  }, [selectedTenant?.name, hasGraphConfig, fetchNetworkClients]);

  // Fetch available PCAP documents for the selected tenant
  const fetchPcapDocuments = useCallback(async () => {
    if (!selectedTenant) {
      setPcapDocuments([]);
      setPcapError(null);
      setPcapLoading(false);
      return;
    }
    try {
      setPcapLoading(true);
      setPcapError(null);
      const response = await authenticatedFetch(
        `${getApiBaseUrl()}/api/documents?tenant=${encodeURIComponent(selectedTenant.name)}`
      );
      const data = await response.json();
      if (!response.ok || !data.success) {
        setPcapError(data.error || 'Failed to fetch documents');
        setPcapDocuments([]);
      } else {
        const docs = Array.isArray(data.documents) ? data.documents : [];
        const pcapDocs = docs.filter((doc) => {
          const name = (doc.name || '').toLowerCase();
          return name.endsWith('.pcap') || name.endsWith('.pcapng') || name.endsWith('.log');
        });
        setPcapDocuments(pcapDocs);
      }
    } catch (e) {
      console.error('Error fetching PCAP documents:', e);
      setPcapError('Failed to fetch documents for PCAP analysis');
      setPcapDocuments([]);
    } finally {
      setPcapLoading(false);
    }
  }, [selectedTenant?.name, authenticatedFetch]);

  useEffect(() => {
    if (selectedTenant?.name) {
      fetchPcapDocuments();
      setSelectedPcapId('');
      setPcapConnections([]);
      setPcapNodes([]);
      setSelectedIpFilter(''); // Reset IP filter when tenant changes
      setSelectedPortFilter(''); // Reset port filter when tenant changes
      setFilterRfc1918(false); // Reset RFC1918 filter when tenant changes
    } else {
      setPcapDocuments([]);
      setSelectedPcapId('');
      setPcapConnections([]);
      setPcapNodes([]);
      setPcapError(null);
      setPcapLoading(false);
      setSelectedIpFilter(''); // Reset IP filter when tenant changes
      setSelectedPortFilter(''); // Reset port filter when tenant changes
      setFilterRfc1918(false); // Reset RFC1918 filter when tenant changes
    }
  }, [selectedTenant?.name, fetchPcapDocuments]);

  // Function to resize graph to fit all nodes (only expands, doesn't move nodes)
  const resizeGraphToFitNodes = useCallback((nodes) => {
    if (!nodes || nodes.length === 0) {
      setPcapGraphWidth(800);
      setPcapGraphHeight(300);
      return;
    }

    const padding = 100; // Padding around nodes
    const nodeRadius = 14; // Radius of the node circle
    
    // Find bounding box of all nodes
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    
    nodes.forEach((node) => {
      minX = Math.min(minX, node.x - nodeRadius);
      maxX = Math.max(maxX, node.x + nodeRadius);
      minY = Math.min(minY, node.y - nodeRadius);
      maxY = Math.max(maxY, node.y + nodeRadius);
    });
    
    // Calculate required dimensions with padding
    const requiredWidth = maxX - minX + padding * 2;
    const requiredHeight = maxY - minY + padding * 2;
    
    // Only expand if nodes are outside current bounds or too close to edges
    const edgeThreshold = 50;
    const needsWidthExpansion = minX < edgeThreshold || maxX > pcapGraphWidth - edgeThreshold || requiredWidth > pcapGraphWidth;
    const needsHeightExpansion = minY < edgeThreshold || maxY > pcapGraphHeight - edgeThreshold || requiredHeight > pcapGraphHeight;
    
    // Calculate new dimensions (only expand, never shrink below minimum)
    const newWidth = needsWidthExpansion ? Math.max(800, requiredWidth) : pcapGraphWidth;
    const newHeight = needsHeightExpansion ? Math.max(300, requiredHeight) : pcapGraphHeight;
    
    // Only update if dimensions actually changed
    if (newWidth !== pcapGraphWidth || newHeight !== pcapGraphHeight) {
      setPcapGraphWidth(newWidth);
      setPcapGraphHeight(newHeight);
    }
    
    // Don't adjust node positions - let them stay where they are
  }, [pcapGraphWidth, pcapGraphHeight]);

  // Initialize PCAP nodes when connections change
  useEffect(() => {
    if (!pcapConnections.length) {
      setPcapNodes([]);
      // Reset graph dimensions when clearing connections
      setPcapGraphWidth(800);
      setPcapGraphHeight(300);
      return;
    }

    const initialWidth = 800;
    const initialHeight = 300;
    const pcapCenterX = initialWidth / 2;
    const pcapCenterY = initialHeight / 2;
    const pcapRadius = 120;

    const ipSet = new Set();
    pcapConnections.forEach((conn) => {
      if (conn.srcIp) ipSet.add(conn.srcIp);
      if (conn.dstIp) ipSet.add(conn.dstIp);
    });
    const ipArray = Array.from(ipSet);
    const nodeCount = ipArray.length || 1;
    
    const nodes = ipArray.map((ip, idx) => {
      const angle = (2 * Math.PI * idx) / nodeCount - Math.PI / 2;
      return {
        ip,
        x: pcapCenterX + pcapRadius * Math.cos(angle),
        y: pcapCenterY + pcapRadius * Math.sin(angle),
      };
    });

    // Set nodes and graph dimensions directly
    setPcapNodes(nodes);
    setPcapGraphWidth(initialWidth);
    setPcapGraphHeight(initialHeight);
  }, [pcapConnections]);

  // Track previous dragIp and current nodes to detect when drag completes
  const prevDragIpRef = useRef(null);
  const nodesRef = useRef(pcapNodes);
  
  // Keep nodesRef updated
  useEffect(() => {
    nodesRef.current = pcapNodes;
  }, [pcapNodes]);
  
  // Resize graph after dragging completes (only when drag ends)
  useEffect(() => {
    // Check if drag just completed (dragIp changed from non-null to null)
    if (prevDragIpRef.current !== null && dragIp === null && nodesRef.current.length > 0) {
      // Resize after drag completes with a small delay
      const timeoutId = setTimeout(() => {
        resizeGraphToFitNodes(nodesRef.current);
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
    
    prevDragIpRef.current = dragIp;
  }, [dragIp, resizeGraphToFitNodes]); // Removed pcapNodes from dependencies to avoid triggering during drag

  const fetchPcapSummary = useCallback(
    async (documentId) => {
      if (!selectedTenant || !documentId) {
        setPcapConnections([]);
        return;
      }
      try {
        setPcapLoading(true);
        setPcapError(null);
        const response = await authenticatedFetch(
          `${getApiBaseUrl()}/api/network/pcap-summary?tenant=${encodeURIComponent(
            selectedTenant.name
          )}&documentId=${encodeURIComponent(documentId)}`
        );
        const data = await response.json();
        if (!response.ok || !data.success) {
          setPcapError(data.error || 'Failed to analyze PCAP file');
          setPcapConnections([]);
        } else {
          setPcapConnections(Array.isArray(data.connections) ? data.connections : []);
        }
      } catch (e) {
        console.error('Error fetching PCAP summary:', e);
        setPcapError('Failed to analyze PCAP file');
        setPcapConnections([]);
      } finally {
        setPcapLoading(false);
      }
    },
    [selectedTenant?.name, authenticatedFetch]
  );

  const fetchFirewallSummary = useCallback(
    async (documentId) => {
      if (!selectedTenant || !documentId) {
        setPcapConnections([]);
        return;
      }
      try {
        setPcapLoading(true);
        setPcapError(null);
        const response = await authenticatedFetch(
          `${getApiBaseUrl()}/api/network/firewall-summary?tenant=${encodeURIComponent(
            selectedTenant.name
          )}&documentId=${encodeURIComponent(documentId)}`
        );
        const data = await response.json();
        if (!response.ok || !data.success) {
          setPcapError(data.error || 'Failed to analyze firewall log');
          setPcapConnections([]);
        } else {
          setPcapConnections(Array.isArray(data.connections) ? data.connections : []);
        }
      } catch (e) {
        console.error('Error fetching firewall summary:', e);
        setPcapError('Failed to analyze firewall log');
        setPcapConnections([]);
      } finally {
        setPcapLoading(false);
      }
    },
    [selectedTenant?.name, authenticatedFetch]
  );

  const handlePcapChange = (e) => {
    const value = e.target.value;
    setSelectedPcapId(value);
    setSelectedIpFilter(''); // Reset IP filter when changing file
    setSelectedPortFilter(''); // Reset port filter when changing file
    setFilterRfc1918(false); // Reset RFC1918 filter when changing file
    if (value) {
      const doc = pcapDocuments.find((d) => String(d.id) === String(value));
      const isLog = doc && (doc.name || '').toLowerCase().endsWith('.log');
      if (isLog) {
        fetchFirewallSummary(value);
      } else {
        fetchPcapSummary(value);
      }
    } else {
      setPcapConnections([]);
    }
  };

  // PCAP drag handlers (must be before early returns)
  // Dynamic center calculations based on current graph dimensions
  const pcapCenterX = pcapGraphWidth / 2;
  const pcapCenterY = pcapGraphHeight / 2;

  // Convert screen coordinates to SVG logical coordinates
  const toLogicalPoint = useCallback((evt) => {
    const svg = pcapSvgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX;
    pt.y = evt.clientY;
    const svgP = pt.matrixTransform(svg.getScreenCTM().inverse());
    // Undo zoom transform around center (use current dynamic center)
    const centerX = pcapGraphWidth / 2;
    const centerY = pcapGraphHeight / 2;
    const x = centerX + (svgP.x - centerX) / pcapZoom;
    const y = centerY + (svgP.y - centerY) / pcapZoom;
    return { x, y };
  }, [pcapZoom, pcapGraphWidth, pcapGraphHeight]);

  const handleNodeMouseDown = useCallback((ip, evt) => {
    evt.preventDefault();
    evt.stopPropagation();
    const logical = toLogicalPoint(evt);
    if (!logical) return;
    const node = pcapNodes.find((n) => n.ip === ip);
    if (!node) return;
    setDragIp(ip);
    setDragOffset({ x: node.x - logical.x, y: node.y - logical.y });
  }, [pcapNodes, toLogicalPoint]);

  const handleSvgMouseMove = useCallback((evt) => {
    if (!dragIp) return;
    evt.preventDefault();
    const logical = toLogicalPoint(evt);
    if (!logical) return;
    const { x, y } = logical;
    
    const newX = x + dragOffset.x;
    const newY = y + dragOffset.y;
    
    // Update node position
    setPcapNodes((prev) =>
      prev.map((n) =>
        n.ip === dragIp ? { ...n, x: newX, y: newY } : n
      )
    );
  }, [dragIp, dragOffset, toLogicalPoint]);

  const handleSvgMouseUp = useCallback(() => {
    setDragIp(null);
  }, []);

  // Helper function to check if IP is broadcast or multicast
  const isBroadcastOrMulticast = useCallback((ip) => {
    if (!ip || typeof ip !== 'string') return false;
    
    // Check for broadcast (255.255.255.255)
    if (ip === '255.255.255.255') return true;
    
    // Check for multicast (224.0.0.0 to 239.255.255.255)
    const parts = ip.split('.');
    if (parts.length !== 4) return false;
    
    const firstOctet = parseInt(parts[0], 10);
    if (isNaN(firstOctet)) return false;
    
    // Multicast range: 224.0.0.0 to 239.255.255.255
    if (firstOctet >= 224 && firstOctet <= 239) return true;
    
    return false;
  }, []);

  // Helper function to check if IP is RFC1918 (private IP address)
  const isRfc1918 = useCallback((ip) => {
    if (!ip || typeof ip !== 'string') return false;
    
    const parts = ip.split('.');
    if (parts.length !== 4) return false;
    
    const octets = parts.map(oct => parseInt(oct, 10));
    if (octets.some(oct => isNaN(oct) || oct < 0 || oct > 255)) return false;
    
    const [first, second] = octets;
    
    // 10.0.0.0/8 (10.0.0.0 to 10.255.255.255)
    if (first === 10) return true;
    
    // 172.16.0.0/12 (172.16.0.0 to 172.31.255.255)
    if (first === 172 && second >= 16 && second <= 31) return true;
    
    // 192.168.0.0/16 (192.168.0.0 to 192.168.255.255)
    if (first === 192 && second === 168) return true;
    
    return false;
  }, []);

  // Extract unique source IPs for the filter dropdown
  const uniqueSourceIps = useMemo(() => {
    const ipSet = new Set();
    pcapConnections.forEach((conn) => {
      if (conn.srcIp) ipSet.add(conn.srcIp);
    });
    return Array.from(ipSet).sort();
  }, [pcapConnections]);

  // Extract unique destination ports for the filter dropdown
  const uniqueDestinationPorts = useMemo(() => {
    const portSet = new Set();
    pcapConnections.forEach((conn) => {
      if (conn.dstPort != null && conn.dstPort !== undefined) {
        portSet.add(Number(conn.dstPort));
      }
    });
    return Array.from(portSet).sort((a, b) => a - b);
  }, [pcapConnections]);

  // Filter connections based on toggle and IP filter
  const filteredConnections = useMemo(() => {
    let filtered = pcapConnections;

    // Apply broadcast/multicast filter
    if (filterBroadcastMulticast) {
      filtered = filtered.filter((conn) => {
        const srcIsBcastMulticast = isBroadcastOrMulticast(conn.srcIp);
        const dstIsBcastMulticast = isBroadcastOrMulticast(conn.dstIp);
        return !srcIsBcastMulticast && !dstIsBcastMulticast;
      });
    }

    // Apply RFC1918 filter (only show traffic between private IPs)
    if (filterRfc1918) {
      filtered = filtered.filter((conn) => {
        const srcIsRfc1918 = isRfc1918(conn.srcIp);
        const dstIsRfc1918 = isRfc1918(conn.dstIp);
        return srcIsRfc1918 && dstIsRfc1918;
      });
    }

    // Apply IP filter (if selected)
    if (selectedIpFilter) {
      filtered = filtered.filter((conn) => {
        return conn.srcIp === selectedIpFilter || conn.dstIp === selectedIpFilter;
      });
    }

    // Apply destination port filter (if selected)
    if (selectedPortFilter) {
      const portNum = parseInt(selectedPortFilter, 10);
      if (!isNaN(portNum)) {
        filtered = filtered.filter((conn) => conn.dstPort === portNum);
      }
    }

    return filtered;
  }, [pcapConnections, filterBroadcastMulticast, filterRfc1918, selectedIpFilter, selectedPortFilter, isBroadcastOrMulticast, isRfc1918]);

  // Filter nodes to only show those that have connections after filtering
  const visibleNodes = useMemo(() => {
    if (!filteredConnections.length) return [];
    
    const visibleIpSet = new Set();
    filteredConnections.forEach((conn) => {
      if (conn.srcIp) visibleIpSet.add(conn.srcIp);
      if (conn.dstIp) visibleIpSet.add(conn.dstIp);
    });
    
    return pcapNodes.filter((node) => visibleIpSet.has(node.ip));
  }, [pcapNodes, filteredConnections]);

  // Group connections for firewall rules view: by destination IP, destination port, action, and protocol
  const groupedFirewallRules = useMemo(() => {
    const groups = new Map();
    filteredConnections.forEach((conn) => {
      const dstIp = conn.dstIp || '';
      const dstPort = conn.dstPort != null ? conn.dstPort : '';
      const action = conn.action || '';
      const protocol = (conn.protocol || '').toUpperCase() || '';
      const key = `${dstIp}|${dstPort}|${action}|${protocol}`;
      if (!groups.has(key)) {
        groups.set(key, {
          dstIp,
          dstPort: conn.dstPort,
          action: conn.action,
          protocol: conn.protocol,
          sources: new Set()
        });
      }
      if (conn.srcIp) groups.get(key).sources.add(conn.srcIp);
    });
    return Array.from(groups.values()).map((g) => ({
      ...g,
      sources: Array.from(g.sources).sort()
    }));
  }, [filteredConnections]);

  // Group connections by destination IP to find all ports connecting to each destination
  const portsByDestination = useMemo(() => {
    const portsMap = {};
    filteredConnections.forEach((conn) => {
      if (!conn.dstIp) return;
      if (!portsMap[conn.dstIp]) {
        portsMap[conn.dstIp] = new Set();
      }
      if (conn.dstPort !== null && conn.dstPort !== undefined) {
        const proto = (conn.protocol || '').toLowerCase();
        portsMap[conn.dstIp].add(`${proto}/${conn.dstPort}`);
      }
    });
    // Convert Sets to sorted arrays
    const result = {};
    Object.keys(portsMap).forEach((ip) => {
      result[ip] = Array.from(portsMap[ip]).sort();
    });
    return result;
  }, [filteredConnections]);

  // Calculate chart dimensions and data
  const chartWidth = 800;
  const chartHeight = 400;
  const padding = { top: 40, right: 40, bottom: 60, left: 60 };

  const getMaxValue = () => {
    if (timeline.length === 0) return 10;
    return Math.max(
      ...timeline.map(d => Math.max(d.connectedCount || 0, d.discoveredCount || 0, d.managedCount || 0)),
      10
    );
  };

  const maxValue = getMaxValue();
  const graphWidth = chartWidth - padding.left - padding.right;
  const graphHeight = chartHeight - padding.top - padding.bottom;

  const getX = (date, index) => {
    if (timeline.length === 0) return padding.left;
    return padding.left + (index / (timeline.length - 1 || 1)) * graphWidth;
  };

  const getY = (value) => {
    return padding.top + graphHeight - (value / maxValue) * graphHeight;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Generate path data for connectedAt line (only used when graph is shown)
  const connectedPath = timeline
    .map((point, index) => {
      const x = getX(point.date, index);
      const y = getY(point.connectedCount || 0);
      return index === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
    })
    .join(' ');

  // Generate path data for discovered line
  const discoveredPath = timeline
    .map((point, index) => {
      const x = getX(point.date, index);
      const y = getY(point.discoveredCount || 0);
      return index === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
    })
    .join(' ');

  // Generate path data for managed line
  const managedPath = timeline
    .map((point, index) => {
      const x = getX(point.date, index);
      const y = getY(point.managedCount || 0);
      return index === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
    })
    .join(' ');

  // Create lookup map for visible nodes
  const pcapNodeByIp = {};
  visibleNodes.forEach((node) => {
    pcapNodeByIp[node.ip] = node;
  });

  const renderPcapGraph = () => {
    if (!filteredConnections.length || !visibleNodes.length) {
      return null;
    }

    // Use dynamic center values
    const centerX = pcapGraphWidth / 2;
    const centerY = pcapGraphHeight / 2;
    const zoomTransform = `translate(${centerX},${centerY}) scale(${pcapZoom}) translate(${-centerX},${-centerY})`;

    return (
      <div className="pcap-graph-wrapper">
        <svg
          ref={pcapSvgRef}
          width="100%"
          height={pcapGraphHeight}
          viewBox={`0 0 ${pcapGraphWidth} ${pcapGraphHeight}`}
          className="pcap-graph"
          onMouseMove={handleSvgMouseMove}
          onMouseUp={handleSvgMouseUp}
          onMouseLeave={handleSvgMouseUp}
        >
          <defs>
            <marker
              id="pcap-arrow"
              markerWidth="10"
              markerHeight="10"
              refX="10"
              refY="3"
              orient="auto"
              markerUnits="strokeWidth"
            >
              <path d="M0,0 L10,3 L0,6 z" fill="#666" />
            </marker>
          </defs>
          <g transform={zoomTransform}>
            {/* Edges */}
            {filteredConnections.map((conn, idx) => {
              const srcNode = pcapNodeByIp[conn.srcIp];
              const dstNode = pcapNodeByIp[conn.dstIp];
              if (!srcNode || !dstNode) {
                return null;
              }
              const weight =
                1 + Math.min(4, Math.log10((conn.packetCount || 1) + 1));

              const midX = (srcNode.x + dstNode.x) / 2;
              const midY = (srcNode.y + dstNode.y) / 2;
              const proto = (conn.protocol || '').toLowerCase();
              const label =
                proto && (conn.dstPort || conn.dstPort === 0)
                  ? `${proto}/${conn.dstPort}`
                  : proto || '';

              return (
                <g key={`edge-${idx}`}>
                  <line
                    x1={srcNode.x}
                    y1={srcNode.y}
                    x2={dstNode.x}
                    y2={dstNode.y}
                    stroke="#999"
                    strokeWidth={weight}
                    strokeOpacity="0.7"
                    markerEnd="url(#pcap-arrow)"
                  />
                  {label && (
                    <text
                      x={midX}
                      y={midY - 6}
                      textAnchor="middle"
                      fontSize="10"
                      fill="#555"
                      className="pcap-edge-label"
                      pointerEvents="none"
                    >
                      {label}
                    </text>
                  )}
                </g>
              );
            })}

            {/* Nodes */}
            {visibleNodes.map((node) => {
              const ports = portsByDestination[node.ip] || [];
              const hasMultiplePorts = ports.length > 1;
              
              return (
                <g key={node.ip} className="pcap-node-group">
                  <circle
                    cx={node.x}
                    cy={node.y}
                    r={14}
                    fill="#007bff"
                    fillOpacity="0.85"
                    stroke="#ffffff"
                    strokeWidth="2"
                    cursor="move"
                    onMouseDown={(evt) => handleNodeMouseDown(node.ip, evt)}
                    onMouseEnter={(evt) => {
                      if (hasMultiplePorts) {
                        setHoveredNodeIp(node.ip);
                        setHoveredNodePosition({
                          x: node.x,
                          y: node.y
                        });
                      }
                    }}
                    onMouseLeave={() => {
                      setHoveredNodeIp(null);
                    }}
                  />
                  <text
                    x={node.x}
                    y={node.y - 20}
                    textAnchor="middle"
                    fontSize="11"
                    fill="#333"
                    pointerEvents="none"
                    className="pcap-node-label"
                  >
                    {node.ip}
                  </text>
                </g>
              );
            })}
          </g>
          
          {/* Ports tooltip - outside zoom transform for consistent size */}
          {hoveredNodeIp && portsByDestination[hoveredNodeIp] && portsByDestination[hoveredNodeIp].length > 1 && (
            <g className="pcap-ports-tooltip">
              {/* Convert node coordinates through zoom transform */}
              {(() => {
                const centerX = pcapGraphWidth / 2;
                const centerY = pcapGraphHeight / 2;
                const transformedX = centerX + (hoveredNodePosition.x - centerX) * pcapZoom;
                const transformedY = centerY + (hoveredNodePosition.y - centerY) * pcapZoom;
                const portCount = portsByDestination[hoveredNodeIp].length;
                const tooltipHeight = Math.max(35, portCount * 18 + 20);
                const rectTop = transformedY - tooltipHeight - 10;
                
                return (
                  <>
                    <foreignObject
                      x={transformedX - 80}
                      y={rectTop}
                      width="160"
                      height={tooltipHeight}
                    >
                      <div
                        xmlns="http://www.w3.org/1999/xhtml"
                        style={{
                          background: 'rgba(0, 0, 0, 0.95)',
                          color: '#ffffff',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          border: '2px solid #ffffff',
                          fontSize: '12px',
                          fontFamily: 'Arial, sans-serif',
                          textAlign: 'center',
                          lineHeight: '1.5'
                        }}
                      >
                        <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '13px' }}>
                          Ports:
                        </div>
                        {portsByDestination[hoveredNodeIp].map((port, idx) => (
                          <div 
                            key={idx} 
                            style={{ 
                              fontSize: '11px', 
                              marginTop: idx === 0 ? '0px' : '6px',
                              display: 'block',
                              width: '100%'
                            }}
                          >
                            {port}
                          </div>
                        ))}
                      </div>
                    </foreignObject>
                  </>
                );
              })()}
            </g>
          )}
        </svg>
      </div>
    );
  };

  const showGraph = hasGraphConfig && timeline.length > 0;
  const showGraphLoading = hasGraphConfig && loading;
  const showGraphError = hasGraphConfig && error;
  const showGraphEmpty = hasGraphConfig && !loading && !error && timeline.length === 0;

  return (
    <div className="utilization-container">
      <h2>Network Utilization</h2>
      {hasGraphConfig && (
        <>
          {showGraphLoading && (
            <div className="chart-container">
              <div className="loading-message">Loading network clients data...</div>
            </div>
          )}
          {showGraphError && (
            <div className="chart-container">
              <div className="error-message">{error}</div>
            </div>
          )}
          {showGraphEmpty && (
            <div className="chart-container">
              <div className="empty-message">No network clients data available.</div>
            </div>
          )}
        </>
      )}
      {showGraph && (
      <div className="chart-container" style={{ position: 'relative' }}>
        <svg width={chartWidth} height={chartHeight} className="timeline-chart" style={{ background: 'white' }}>
          {/* Grid lines */}
          {[0, 1, 2, 3, 4, 5].map(i => {
            const y = padding.top + (graphHeight / 5) * i;
            return (
              <line
                key={`grid-${i}`}
                x1={padding.left}
                y1={y}
                x2={padding.left + graphWidth}
                y2={y}
                stroke="#e0e0e0"
                strokeWidth="1"
              />
            );
          })}

          {/* Y-axis labels */}
          {[0, 1, 2, 3, 4, 5].map(i => {
            const value = Math.round((maxValue / 5) * (5 - i));
            const y = padding.top + (graphHeight / 5) * i;
            return (
              <text
                key={`y-label-${i}`}
                x={padding.left - 10}
                y={y + 5}
                textAnchor="end"
                fontSize="12"
                fill="#333"
                className="axis-label"
              >
                {value}
              </text>
            );
          })}

          {/* X-axis labels */}
          {timeline.map((point, index) => {
            if (index % Math.ceil(timeline.length / 8) !== 0 && index !== timeline.length - 1) {
              return null;
            }
            const x = getX(point.date, index);
            return (
              <text
                key={`x-label-${index}`}
                x={x}
                y={chartHeight - padding.bottom + 20}
                textAnchor="middle"
                fontSize="12"
                fill="#333"
                className="axis-label"
              >
                {formatDate(point.date)}
              </text>
            );
          })}

          {/* ConnectedAt line */}
          <path
            d={connectedPath}
            fill="none"
            stroke="#007bff"
            strokeWidth="2"
            className="timeline-line connected-line"
          />

          {/* Discovered line */}
          <path
            d={discoveredPath}
            fill="none"
            stroke="#28a745"
            strokeWidth="2"
            className="timeline-line discovered-line"
          />

          {/* Managed line */}
          <path
            d={managedPath}
            fill="none"
            stroke="#ff6b35"
            strokeWidth="2"
            className="timeline-line managed-line"
          />

          {/* ConnectedAt points */}
          {timeline.map((point, index) => {
            const x = getX(point.date, index);
            const y = getY(point.connectedCount || 0);
            return (
              <circle
                key={`connected-point-${index}`}
                cx={x}
                cy={y}
                r="3"
                fill="#007bff"
                className="data-point"
                onMouseEnter={(e) => {
                  const svg = e.currentTarget.ownerSVGElement;
                  const svgRect = svg.getBoundingClientRect();
                  const pointRect = e.currentTarget.getBoundingClientRect();
                  setTooltip({
                    show: true,
                    x: pointRect.left - svgRect.left + (pointRect.width / 2),
                    y: pointRect.top - svgRect.top - 10,
                    text: `Connected At: ${point.connectedCount || 0}`
                  });
                }}
                onMouseLeave={() => setTooltip({ show: false, x: 0, y: 0, text: '' })}
              />
            );
          })}

          {/* Discovered points */}
          {timeline.map((point, index) => {
            const x = getX(point.date, index);
            const y = getY(point.discoveredCount || 0);
            return (
              <circle
                key={`discovered-point-${index}`}
                cx={x}
                cy={y}
                r="3"
                fill="#28a745"
                className="data-point"
                onMouseEnter={(e) => {
                  const svg = e.currentTarget.ownerSVGElement;
                  const svgRect = svg.getBoundingClientRect();
                  const pointRect = e.currentTarget.getBoundingClientRect();
                  setTooltip({
                    show: true,
                    x: pointRect.left - svgRect.left + (pointRect.width / 2),
                    y: pointRect.top - svgRect.top - 10,
                    text: `Discovered: ${point.discoveredCount || 0}`
                  });
                }}
                onMouseLeave={() => setTooltip({ show: false, x: 0, y: 0, text: '' })}
              />
            );
          })}

          {/* Managed points */}
          {timeline.map((point, index) => {
            const x = getX(point.date, index);
            const y = getY(point.managedCount || 0);
            return (
              <circle
                key={`managed-point-${index}`}
                cx={x}
                cy={y}
                r="3"
                fill="#ff6b35"
                className="data-point"
                onMouseEnter={(e) => {
                  const svg = e.currentTarget.ownerSVGElement;
                  const svgRect = svg.getBoundingClientRect();
                  const pointRect = e.currentTarget.getBoundingClientRect();
                  setTooltip({
                    show: true,
                    x: pointRect.left - svgRect.left + (pointRect.width / 2),
                    y: pointRect.top - svgRect.top - 10,
                    text: `Managed: ${point.managedCount || 0}`
                  });
                }}
                onMouseLeave={() => setTooltip({ show: false, x: 0, y: 0, text: '' })}
              />
            );
          })}
        </svg>

        {/* Tooltip */}
        {tooltip.show && (
          <div
            className="chart-tooltip"
            style={{
              position: 'absolute',
              left: `${tooltip.x}px`,
              top: `${tooltip.y}px`,
              transform: 'translate(-50%, -100%)'
            }}
          >
            {tooltip.text}
          </div>
        )}

        {/* Legend */}
        <div className="chart-legend">
          <div className="legend-item legend-item-connected">
            <div className="legend-color connected-color"></div>
            <span>Connected At</span>
          </div>
          <div className="legend-item legend-item-discovered">
            <div className="legend-color discovered-color"></div>
            <span>Discovered</span>
          </div>
          <div className="legend-item legend-item-managed">
            <div className="legend-color managed-color"></div>
            <span>Managed</span>
          </div>
        </div>

        {/* Supporting text */}
        <div className="chart-supporting-text">
          <h3>How to read the graph:</h3>
          <ul>
            <li><strong>Connected At</strong>: An indicator of when a client is connected</li>
            <li><strong>Discovered</strong>: This shows the total number of clients seen on the network</li>
            <li><strong>Managed</strong>: Indicates how many discovered clients are enrolled in MDM</li>
          </ul>
        </div>
      </div>
      )}

      {/* PCAP communication analysis - always visible */}
      <div className="pcap-section">
        <h3>Topology</h3>
        <div className="pcap-capture-hint">
          <strong>Tip:</strong> For PCAP files, use this filter for best results:{' '}
          <code className="pcap-filter-code">((tcp[tcpflags] &amp; tcp-syn != 0) or udp) and not broadcast and not multicast</code>
          . Windows Firewall logs (pfirewall.log) are also supported.
        </div>
        <div className="pcap-controls">
          <label htmlFor="pcap-select">Select file:</label>
          <select
            id="pcap-select"
            value={selectedPcapId}
            onChange={handlePcapChange}
            disabled={pcapLoading || pcapDocuments.length === 0}
          >
            <option value="">-- Select PCAP or firewall log --</option>
            {pcapDocuments.map((doc) => (
              <option key={doc.id} value={doc.id}>
                {doc.name}
              </option>
            ))}
          </select>
        </div>
        {pcapLoading && (
          <div className="pcap-status">Analyzing communication...</div>
        )}
        {pcapError && (
          <div className="pcap-error">{pcapError}</div>
        )}
        {!pcapLoading && !pcapError && selectedPcapId && pcapConnections.length === 0 && (
          <div className="pcap-status">No TCP/UDP connections found in this file.</div>
        )}
        {!pcapLoading && !pcapError && pcapConnections.length > 0 && (
          <div className="pcap-visualization">
            <div className="pcap-controls-row">
              <div className="pcap-zoom-controls">
                <label htmlFor="pcap-zoom">Zoom:</label>
                <input
                  id="pcap-zoom"
                  type="range"
                  min="0.5"
                  max="2"
                  step="0.1"
                  value={pcapZoom}
                  onChange={(e) => setPcapZoom(Number(e.target.value))}
                />
                <span>{Math.round(pcapZoom * 100)}%</span>
              </div>
              <div className="pcap-ip-filter-controls">
                <label htmlFor="pcap-ip-filter">Filter by IP:</label>
                <select
                  id="pcap-ip-filter"
                  value={selectedIpFilter}
                  onChange={(e) => setSelectedIpFilter(e.target.value)}
                >
                  <option value="">-- All IPs --</option>
                  {uniqueSourceIps.map((ip) => (
                    <option key={ip} value={ip}>
                      {ip}
                    </option>
                  ))}
                </select>
              </div>
              <div className="pcap-ip-filter-controls">
                <label htmlFor="pcap-port-filter">Filter by destination port:</label>
                <select
                  id="pcap-port-filter"
                  value={selectedPortFilter}
                  onChange={(e) => setSelectedPortFilter(e.target.value)}
                >
                  <option value="">-- All ports --</option>
                  {uniqueDestinationPorts.map((port) => (
                    <option key={port} value={port}>
                      {port}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="pcap-controls-row pcap-toggle-row">
              <button
                type="button"
                role="switch"
                aria-checked={filterBroadcastMulticast}
                aria-label="Hide Broadcast/Multicast"
                className={`pcap-smart-toggle ${filterBroadcastMulticast ? 'on' : 'off'}`}
                onClick={() => setFilterBroadcastMulticast(!filterBroadcastMulticast)}
              >
                <span className="pcap-smart-toggle-thumb" />
                <span className="pcap-smart-toggle-label">
                  {filterBroadcastMulticast ? 'Hide Broadcast/Multicast' : 'Show Broadcast/Multicast'}
                </span>
              </button>
              <button
                type="button"
                role="switch"
                aria-checked={filterRfc1918}
                aria-label="Only RFC1918 (Private IPs)"
                className={`pcap-smart-toggle ${filterRfc1918 ? 'on' : 'off'}`}
                onClick={() => setFilterRfc1918(!filterRfc1918)}
              >
                <span className="pcap-smart-toggle-thumb" />
                <span className="pcap-smart-toggle-label">
                  {filterRfc1918 ? 'Only RFC1918 (Private IPs)' : 'All IPs'}
                </span>
              </button>
              <button
                type="button"
                role="switch"
                aria-checked={viewAsFirewallRules}
                aria-label="View as firewall rules"
                className={`pcap-smart-toggle ${viewAsFirewallRules ? 'on' : 'off'}`}
                onClick={() => setViewAsFirewallRules(!viewAsFirewallRules)}
              >
                <span className="pcap-smart-toggle-thumb" />
                <span className="pcap-smart-toggle-label">
                  {viewAsFirewallRules ? 'Firewall rules view' : 'Graph view'}
                </span>
              </button>
            </div>
            {viewAsFirewallRules ? (
              <div className="pcap-firewall-rules-table-wrapper">
                <table className="pcap-firewall-rules-table">
                  <thead>
                    <tr>
                      <th>Source</th>
                      <th>Destination</th>
                      <th>Destination Port</th>
                      <th>Protocol</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedFirewallRules.map((rule, idx) => (
                      <tr key={idx}>
                        <td>{rule.sources.length > 0 ? rule.sources.join(', ') : '—'}</td>
                        <td>{rule.dstIp || '—'}</td>
                        <td>{rule.dstPort != null ? rule.dstPort : '—'}</td>
                        <td>{(rule.protocol || '').toUpperCase() || '—'}</td>
                        <td>
                          {rule.action ? (
                            <span className={`firewall-action firewall-action-${(rule.action || '').toLowerCase()}`}>
                              {rule.action}
                            </span>
                          ) : (
                            <span className="null-value">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              renderPcapGraph()
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Utilization;
